<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import AceleresTeamPresenceAPI from 'dashboard/api/aceleresTeamPresence';
import { frontendURL } from 'dashboard/helper/URLHelper';

const BRAZIL_TIME_ZONE = 'America/Sao_Paulo';
const route = useRoute();
const router = useRouter();
const accountId = route.params.accountId;

const formatDateInput = value => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: BRAZIL_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(value);
  const part = type => parts.find(item => item.type === type)?.value;
  return `${part('year')}-${part('month')}-${part('day')}`;
};
const todayInput = () => formatDateInput(new Date());
const formatClock = value =>
  new Intl.DateTimeFormat('pt-BR', {
    timeZone: BRAZIL_TIME_ZONE,
    hour: '2-digit',
    minute: '2-digit',
  }).format(value);
const formatDay = date => date.split('-').reverse().join('/');
const formatLongDate = date =>
  new Intl.DateTimeFormat('pt-BR', {
    timeZone: BRAZIL_TIME_ZONE,
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(new Date(`${date}T12:00:00-03:00`));

const loading = ref(true);
const hasError = ref(false);
const fromDate = ref(todayInput());
const toDate = ref(todayInput());
const customPeriod = ref(false);
const fromDateInput = ref(null);
const historyDays = ref([]);
const expandedSellers = ref({});
const lastUpdated = ref(null);
const periodOptions = [
  { label: 'Hoje', days: 1 },
  { label: '7 dias', days: 7 },
  { label: '30 dias', days: 30 },
];

const fetchHistory = async () => {
  if (!fromDate.value) fromDate.value = todayInput();
  if (!toDate.value) toDate.value = fromDate.value;
  if (fromDate.value > toDate.value) {
    [fromDate.value, toDate.value] = [toDate.value, fromDate.value];
  }
  loading.value = true;
  try {
    const { data } = await AceleresTeamPresenceAPI.getHistory({
      from: fromDate.value,
      to: toDate.value,
    });
    historyDays.value = data.days || [];
    lastUpdated.value = data.generated_at
      ? new Date(data.generated_at)
      : new Date();
    hasError.value = false;
  } catch (error) {
    hasError.value = true;
  } finally {
    loading.value = false;
  }
};
onMounted(fetchHistory);

const goBack = () =>
  router.push(frontendURL(`accounts/${accountId}/equipe-online`));
const selectPeriod = async days => {
  const end = new Date(`${todayInput()}T12:00:00-03:00`);
  const start = new Date(end);
  start.setDate(end.getDate() - days + 1);
  fromDate.value = formatDateInput(start);
  toDate.value = formatDateInput(end);
  customPeriod.value = false;
  await fetchHistory();
};
const selectCustomPeriod = () => {
  customPeriod.value = true;
  fromDateInput.value?.focus();
};
const updateCustomPeriod = async () => {
  customPeriod.value = false;
  await fetchHistory();
};
const rangeDays = computed(() => {
  if (!fromDate.value || !toDate.value) return null;
  const from = new Date(`${fromDate.value}T12:00:00-03:00`);
  const to = new Date(`${toDate.value}T12:00:00-03:00`);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;
  return Math.round((to - from) / 86400000) + 1;
});
const activePeriod = computed(() => {
  if (customPeriod.value) return 'custom';
  const today = todayInput();
  if (fromDate.value === today && toDate.value === today) return 1;
  return [7, 30].includes(rangeDays.value) ? rangeDays.value : 'custom';
});
const periodLabel = computed(() => {
  if (fromDate.value === toDate.value)
    return fromDate.value === todayInput()
      ? 'Hoje'
      : formatLongDate(fromDate.value);
  return `${formatLongDate(fromDate.value)} até ${formatLongDate(toDate.value)}`;
});
const dayCountLabel = computed(() =>
  historyDays.value.length === 1 ? '1 dia' : `${historyDays.value.length} dias`
);
const overallSummary = computed(() =>
  historyDays.value.reduce(
    (total, day) => {
      total.received += day.summary?.received_today || 0;
      total.answered += day.summary?.answered_today || 0;
      total.waiting += day.summary?.waiting_today || 0;
      const average = day.summary?.avg_response_minutes;
      if (average !== null && average !== undefined) {
        total.avgTotal += average;
        total.avgSamples += 1;
      }
      return total;
    },
    { received: 0, answered: 0, waiting: 0, avgTotal: 0, avgSamples: 0 }
  )
);
const formatResponse = value => {
  if (value === null || value === undefined) return '—';
  if (value < 60) return `${value} min`;
  const hours = Math.floor(value / 60);
  const minutes = value % 60;
  return minutes ? `${hours}h ${minutes}min` : `${hours}h`;
};
const averageResponse = computed(() =>
  overallSummary.value.avgSamples
    ? formatResponse(
        Math.round(
          overallSummary.value.avgTotal / overallSummary.value.avgSamples
        )
      )
    : '—'
);
const formatDuration = value => {
  const seconds = Number(value) || 0;
  if (seconds <= 0) return '0min';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours && minutes) return `${hours}h ${minutes}min`;
  if (hours) return `${hours}h`;
  return `${Math.max(1, minutes)}min`;
};
const statusSeconds = (seller, status) =>
  seller.statuses.find(row => row.status === status)?.total_seconds || 0;
const presenceTotal = (day, status) => day.summary?.presence?.[status] || 0;
const presenceWidth = (day, status) => {
  const total = ['active', 'idle', 'offline'].reduce(
    (sum, key) => sum + presenceTotal(day, key),
    0
  );
  return total ? `${(presenceTotal(day, status) / total) * 100}%` : '0%';
};
const presenceSellers = day =>
  [...(day.presence_history || [])]
    .filter(seller => seller.total_seconds > 0)
    .sort(
      (a, b) =>
        statusSeconds(b, 'active') - statusSeconds(a, 'active') ||
        a.name.localeCompare(b.name, 'pt-BR')
    );
const dayStartEpoch = day =>
  new Date(`${day.date}T00:00:00-03:00`).getTime() / 1000;
const timelineSegments = (day, seller) =>
  seller.statuses
    .filter(status => status.status !== 'offline')
    .flatMap(status =>
      status.intervals.map(interval => ({
        key: `${status.status}-${interval.start_at}-${interval.end_at}`,
        color: status.status === 'active' ? '#16a34a' : '#f59e0b',
        left: `${Math.max(0, ((interval.start_at - dayStartEpoch(day)) / 86400) * 100)}%`,
        width: `${Math.max(0, ((interval.end_at - interval.start_at) / 86400) * 100)}%`,
      }))
    );
const nowPosition = day =>
  day.date === todayInput() && lastUpdated.value
    ? `${Math.min(100, Math.max(0, ((lastUpdated.value.getTime() / 1000 - dayStartEpoch(day)) / 86400) * 100))}%`
    : null;
const sellerInitials = name =>
  (name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0] || '')
    .join('')
    .toUpperCase();
const sellerExpanded = (date, userId) =>
  !!expandedSellers.value[`${date}:${userId}`];
const toggleSeller = (date, userId) => {
  const key = `${date}:${userId}`;
  expandedSellers.value = {
    ...expandedSellers.value,
    [key]: !expandedSellers.value[key],
  };
};
const overviewSections = day => [
  {
    key: 'waiting',
    title: 'Aguardando resposta',
    empty: 'Nenhum lead pendente neste dia.',
    leads: day.overview?.waiting || [],
  },
  {
    key: 'answered',
    title: 'Atendidos no dia',
    empty: 'Nenhum atendimento humano neste dia.',
    leads: day.overview?.answered || [],
  },
  {
    key: 'aurora',
    title: 'Atendidos pela Aurora',
    empty: 'Nenhum atendimento da Aurora neste dia.',
    leads: day.overview?.aurora || [],
  },
];
const formatLeadTime = epoch =>
  epoch ? formatClock(new Date(epoch * 1000)) : '—';
</script>

<template>
  <div class="history-page flex flex-col w-full h-full overflow-hidden">
    <header class="history-header">
      <div class="history-heading acl-mobile-account-menu-host">
        <button
          type="button"
          class="history-header-button history-back"
          aria-label="Voltar para equipe online"
          @click="goBack"
        >
          <span class="i-lucide-arrow-left size-4" />
        </button>
        <div class="min-w-0">
          <h1>Histórico da equipe</h1>
          <p>Atendimentos, respostas e tempo online de cada vendedor.</p>
        </div>
      </div>
      <div class="history-control-meta">
        <span v-if="lastUpdated" class="history-updated"
          ><span />Atualizado às {{ formatClock(lastUpdated) }}</span
        >
        <button
          type="button"
          class="history-header-button"
          title="Atualizar"
          aria-label="Atualizar"
          @click="fetchHistory"
        >
          <span class="i-lucide-refresh-cw size-4" />
        </button>
      </div>
      <div class="history-controls">
        <div class="history-periods" aria-label="Período">
          <button
            v-for="option in periodOptions"
            :key="option.days"
            type="button"
            :class="{ active: activePeriod === option.days }"
            :aria-pressed="activePeriod === option.days"
            @click="selectPeriod(option.days)"
          >
            {{ option.label }}
          </button>
          <button
            type="button"
            :class="{ active: activePeriod === 'custom' }"
            :aria-pressed="activePeriod === 'custom'"
            @click="selectCustomPeriod"
          >
            Personalizado
          </button>
        </div>
        <div class="history-dates">
          <label>
            De
            <input
              ref="fromDateInput"
              v-model="fromDate"
              type="date"
              @change="updateCustomPeriod"
            />
          </label>
          <label>
            Até
            <input
              v-model="toDate"
              type="date"
              @change="updateCustomPeriod"
            />
          </label>
        </div>
      </div>
    </header>

    <div class="history-content flex-1 min-h-0 overflow-y-auto scrollbar-thin">
      <section class="history-surface history-summary">
        <div class="history-summary-cell">
          <span>Período</span><strong>{{ dayCountLabel }}</strong
          ><small>{{ periodLabel }}</small>
        </div>
        <div class="history-summary-cell">
          <span>Recebidos</span><strong>{{ overallSummary.received }}</strong>
        </div>
        <div class="history-summary-cell">
          <span>Atendidos</span><strong>{{ overallSummary.answered }}</strong>
        </div>
        <div class="history-summary-cell">
          <span>Aguardando</span><strong>{{ overallSummary.waiting }}</strong>
        </div>
        <div class="history-summary-cell">
          <span>Média 1ª resposta</span><strong>{{ averageResponse }}</strong>
        </div>
      </section>

      <div v-if="loading && !historyDays.length" class="history-state">
        Carregando histórico…
      </div>
      <div v-else-if="hasError" class="history-state">
        Não foi possível carregar o histórico. Tente novamente.
      </div>
      <div v-else-if="!historyDays.length" class="history-state">
        Nenhum dado de histórico disponível.
      </div>
      <div v-else class="history-days">
        <article
          v-for="day in historyDays"
          :key="day.date"
          class="history-surface history-day"
        >
          <div class="history-day-head">
            <div>
              <h2>{{ day.weekday }}</h2>
              <p>
                {{ formatDay(day.date)
                }}<template v-if="day.date === todayInput() && lastUpdated">
                  · até {{ formatClock(lastUpdated) }}</template
                >
              </p>
            </div>
            <div class="history-day-metrics">
              <div>
                <span>Recebidos</span
                ><strong>{{ day.summary?.received_today || 0 }}</strong>
              </div>
              <div>
                <span>Atendidos</span
                ><strong>{{ day.summary?.answered_today || 0 }}</strong>
              </div>
              <div>
                <span>Aguardando</span
                ><strong>{{ day.summary?.waiting_today || 0 }}</strong>
              </div>
              <div>
                <span>Aurora</span
                ><strong>{{ day.summary?.aurora_today || 0 }}</strong>
              </div>
              <div>
                <span>Vencidos</span
                ><strong>{{ day.summary?.missed_today || 0 }}</strong>
              </div>
            </div>
          </div>

          <section class="history-time-team">
            <div class="history-time-heading">
              <h3 class="history-section-label">Tempo da equipe</h3>
              <div class="history-time-legend">
                <span
                  ><i class="online" />Online
                  <strong>{{
                    formatDuration(presenceTotal(day, 'active'))
                  }}</strong></span
                >
                <span
                  ><i class="idle" />Ocioso
                  <strong>{{
                    formatDuration(presenceTotal(day, 'idle'))
                  }}</strong></span
                >
                <span
                  ><i class="offline" />Offline
                  <strong>{{
                    formatDuration(presenceTotal(day, 'offline'))
                  }}</strong></span
                >
              </div>
            </div>
            <div class="history-time-stack">
              <span
                class="online"
                :style="{ width: presenceWidth(day, 'active') }"
              />
              <span
                class="idle"
                :style="{ width: presenceWidth(day, 'idle') }"
              />
              <span
                class="offline"
                :style="{ width: presenceWidth(day, 'offline') }"
              />
            </div>
          </section>

          <section class="history-timeline">
            <div class="history-timeline-heading">
              <h3 class="history-section-label">Linha do tempo por vendedor</h3>
              <span
                >{{ presenceSellers(day).length }} vendedores · clique para ver
                os horários</span
              >
            </div>
            <div class="history-timeline-scroll">
              <div class="history-axis history-three-cols">
                <span />
                <div>
                  <span>00h</span><span>06h</span><span>12h</span
                  ><span>18h</span><span>24h</span>
                </div>
                <span>ONLINE · OCIOSO</span>
              </div>
              <div v-if="!presenceSellers(day).length" class="history-empty">
                Nenhum intervalo de acesso registrado neste dia.
              </div>
              <template
                v-for="seller in presenceSellers(day)"
                :key="`${day.date}-${seller.user_id}`"
              >
                <button
                  type="button"
                  class="history-seller-row history-three-cols"
                  :class="{
                    expanded: sellerExpanded(day.date, seller.user_id),
                  }"
                  :aria-expanded="sellerExpanded(day.date, seller.user_id)"
                  @click="toggleSeller(day.date, seller.user_id)"
                >
                  <span class="history-seller-name"
                    ><span class="history-seller-avatar">{{
                      sellerInitials(seller.name)
                    }}</span
                    ><strong>{{ seller.name }}</strong></span
                  >
                  <span class="history-seller-track">
                    <i
                      v-for="segment in timelineSegments(day, seller)"
                      :key="segment.key"
                      class="history-track-segment"
                      :style="{
                        left: segment.left,
                        width: segment.width,
                        backgroundColor: segment.color,
                      }"
                    />
                    <i
                      v-if="nowPosition(day)"
                      class="history-now-line"
                      :style="{ left: nowPosition(day) }"
                    />
                  </span>
                  <span class="history-seller-totals"
                    ><strong>{{
                      formatDuration(statusSeconds(seller, 'active'))
                    }}</strong
                    ><strong>{{
                      formatDuration(statusSeconds(seller, 'idle'))
                    }}</strong></span
                  >
                </button>
                <div
                  v-if="sellerExpanded(day.date, seller.user_id)"
                  class="history-intervals history-three-cols"
                >
                  <span />
                  <div>
                    <template
                      v-for="status in seller.statuses.filter(
                        item => item.status !== 'offline'
                      )"
                      :key="status.status"
                    >
                      <span
                        v-for="interval in status.intervals"
                        :key="`${status.status}-${interval.start_at}-${interval.end_at}`"
                        class="history-interval-pill"
                        :class="status.status"
                      >
                        {{ interval.start_label }}–{{ interval.end_label }}
                      </span>
                    </template>
                    <span
                      v-if="
                        !statusSeconds(seller, 'active') &&
                        !statusSeconds(seller, 'idle')
                      "
                      class="history-no-interval"
                      >Sem período online ou ocioso.</span
                    >
                  </div>
                  <span />
                </div>
              </template>
            </div>
            <p class="history-timeline-foot">
              <i />{{
                day.date === todayInput() && lastUpdated
                  ? `agora (${formatClock(lastUpdated)})`
                  : '24h'
              }}
              · cinza = offline
            </p>
          </section>

          <div class="history-end-grid">
            <section
              v-for="section in overviewSections(day)"
              :key="section.key"
              class="history-end-card"
            >
              <h3>{{ section.title }}</h3>
              <p v-if="!section.leads.length" class="history-end-empty">
                {{ section.empty }}
              </p>
              <ul v-else>
                <li
                  v-for="lead in section.leads"
                  :key="`${section.key}-${lead.display_id}`"
                >
                  <div>
                    <strong
                      >{{ lead.contact_name }} #{{ lead.display_id }}</strong
                    >
                    <span>{{
                      section.key === 'waiting'
                        ? formatLeadTime(lead.first_inbound_at)
                        : formatResponse(lead.response_minutes)
                    }}</span>
                  </div>
                  <p v-if="section.key === 'waiting'">
                    {{ lead.current_assignee || 'Sem responsável atual'
                    }}<template v-if="lead.passed_by?.length">
                      · passou por {{ lead.passed_by.join(', ') }}</template
                    >
                  </p>
                  <p v-else>
                    {{
                      section.key === 'answered'
                        ? `por ${lead.answered_by}`
                        : lead.answered_by
                    }}
                  </p>
                </li>
              </ul>
            </section>
          </div>
        </article>
      </div>
    </div>
  </div>
</template>

<style scoped>
.history-page {
  background: #f5f5f3;
  color: #1f2328;
  font-family: Inter, sans-serif;
}
.history-header {
  display: grid;
  grid-template-columns: 460px 705px minmax(0, 1fr);
  grid-template-areas: 'heading controls meta';
  align-items: center;
  gap: 8px;
  padding: 32px 36px 20px;
}
.history-heading {
  grid-area: heading;
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.history-heading h1 {
  font-size: 26px;
  line-height: 32px;
  font-weight: 700;
}
.history-heading p {
  margin-top: 2px;
  color: #6b7280;
  font-size: 14px;
}
.history-header-button {
  display: inline-flex;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  flex: none;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  color: #444c56;
}
.history-back {
  border-radius: 12px;
}
.history-header-button:hover {
  background: #fafaf9;
}
.history-controls {
  grid-area: controls;
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 10px;
}
.history-periods {
  display: flex;
  flex: none;
  gap: 2px;
  padding: 3px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #fff;
}
.history-periods button {
  height: 36px;
  padding: 0 10px;
  border-radius: 8px;
  color: #444c56;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}
.history-periods button.active {
  background: #1f2328;
  color: #fff;
}
.history-updated {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: #6b7280;
  font-size: 12px;
  white-space: nowrap;
}
.history-control-meta {
  grid-area: meta;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}
.history-updated span {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #16a34a;
}
.history-dates {
  display: flex;
  flex: none;
  align-items: center;
  gap: 10px;
}
.history-dates label {
  display: flex;
  align-items: center;
  gap: 5px;
  color: #6b7280;
  font-size: 12px;
  white-space: nowrap;
}
.history-dates input {
  width: 136px;
  height: 36px;
  flex: none;
  padding: 0 10px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  color: #1f2328;
  box-sizing: border-box;
}
.history-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding: 0 44px 32px;
}
.history-surface {
  border: 1px solid #eceef1;
  border-radius: 20px;
  background: #fff;
  box-shadow:
    0 1px 3px rgba(16, 24, 40, 0.04),
    0 8px 24px rgba(16, 24, 40, 0.04);
}
.history-summary {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  padding: 18px 0;
}
.history-summary-cell {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 4px;
  padding: 0 20px;
  border-left: 1px solid #f0f1f3;
}
.history-summary-cell:first-child {
  border-left: 0;
}
.history-summary-cell > span {
  color: #6b7280;
  font-size: 12px;
}
.history-summary-cell strong {
  color: #1f2328;
  font-size: 26px;
  line-height: 30px;
  font-weight: 700;
}
.history-summary-cell small {
  color: #8c959f;
  font-size: 12px;
}
.history-state {
  padding: 60px 20px;
  color: #8c959f;
  text-align: center;
  font-size: 13px;
}
.history-days {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.history-day {
  overflow: hidden;
}
.history-day-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 18px 22px;
  border-bottom: 1px solid #f0f1f3;
}
.history-day-head h2 {
  font-size: 16px;
  font-weight: 600;
}
.history-day-head p {
  margin-top: 3px;
  color: #6b7280;
  font-size: 12px;
  white-space: nowrap;
}
.history-day-metrics {
  display: flex;
  flex-wrap: wrap;
  gap: 32px;
}
.history-day-metrics div {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.history-day-metrics span {
  color: #6b7280;
  font-size: 11px;
}
.history-day-metrics strong {
  font-size: 18px;
  line-height: 21px;
  font-weight: 700;
}
.history-time-team {
  padding: 18px 22px;
  border-bottom: 1px solid #f0f1f3;
}
.history-section-label {
  color: #8c959f;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.history-time-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
}
.history-time-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}
.history-time-legend span {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #6b7280;
  font-size: 12px;
}
.history-time-legend strong {
  color: #1f2328;
  font-weight: 700;
}
.history-time-legend i {
  width: 10px;
  height: 10px;
  border-radius: 2px;
}
.online {
  background: #16a34a;
}
.idle {
  background: #f59e0b;
}
.offline {
  background: #d5d9de;
}
.history-time-stack {
  display: flex;
  height: 10px;
  overflow: hidden;
  margin-top: 15px;
  border-radius: 999px;
  background: #f1f2f4;
}
.history-time-stack span {
  height: 100%;
}
.history-timeline {
  padding: 18px 22px 20px;
}
.history-timeline-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.history-timeline-heading > span {
  color: #6b7280;
  font-size: 12px;
}
.history-timeline-scroll {
  margin-top: 14px;
  overflow-x: auto;
}
.history-three-cols {
  display: grid;
  grid-template-columns: 200px minmax(0, 1fr) 190px;
  align-items: center;
  column-gap: 20px;
}
.history-axis {
  padding: 0 0 6px;
  color: #8c959f;
  font-size: 10px;
}
.history-axis > div {
  display: flex;
  justify-content: space-between;
}
.history-axis > span:last-child {
  text-align: right;
}
.history-seller-row {
  width: 100%;
  min-height: 50px;
  padding: 10px 0;
  border-top: 1px solid #f5f5f3;
  text-align: left;
}
.history-seller-row:hover,
.history-seller-row.expanded,
.history-intervals {
  background: #fafaf9;
}
.history-seller-name {
  display: flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
}
.history-seller-name strong {
  overflow: hidden;
  font-size: 13px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.history-seller-avatar {
  display: inline-flex;
  width: 30px;
  height: 30px;
  flex: none;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #f1f2f4;
  color: #444c56;
  font-size: 10px;
  font-weight: 700;
}
.history-seller-track {
  position: relative;
  display: block;
  height: 18px;
  overflow: visible;
  border-radius: 6px;
  background: linear-gradient(
      90deg,
      transparent calc(25% - 0.5px),
      #f0f1f3 25%,
      transparent calc(25% + 0.5px),
      transparent calc(50% - 0.5px),
      #f0f1f3 50%,
      transparent calc(50% + 0.5px),
      transparent calc(75% - 0.5px),
      #f0f1f3 75%,
      transparent calc(75% + 0.5px)
    ),
    #eef0f2;
}
.history-track-segment {
  position: absolute;
  top: 0;
  height: 18px;
}
.history-now-line {
  position: absolute;
  z-index: 2;
  top: -3px;
  width: 2px;
  height: 24px;
  background: #1f2328;
}
.history-seller-totals {
  display: flex;
  justify-content: flex-end;
  gap: 14px;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.history-seller-totals strong {
  min-width: 65px;
  font-weight: 600;
  text-align: right;
}
.history-seller-totals strong:first-child {
  color: #166534;
}
.history-seller-totals strong:last-child {
  color: #9a4b00;
}
.history-intervals {
  min-height: 40px;
  padding: 4px 0 10px;
}
.history-intervals > div {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.history-interval-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
}
.history-interval-pill.active {
  background: #eef8f1;
  color: #166534;
}
.history-interval-pill.idle {
  background: #fff7e8;
  color: #9a4b00;
}
.history-no-interval {
  color: #8c959f;
  font-size: 11px;
}
.history-empty {
  padding: 24px 0;
  color: #8c959f;
  font-size: 13px;
}
.history-timeline-foot {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-top: 12px;
  color: #8c959f;
  font-size: 11px;
}
.history-timeline-foot i {
  width: 2px;
  height: 12px;
  background: #1f2328;
}
.history-end-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  padding: 0 22px 22px;
}
.history-end-card {
  min-height: 100px;
  padding: 18px 20px;
  border: 1px solid #eceef1;
  border-radius: 16px;
  background: #fff;
}
.history-end-card h3 {
  font-size: 14px;
  font-weight: 600;
}
.history-end-empty {
  margin-top: 10px;
  color: #8c959f;
  font-size: 13px;
}
.history-end-card ul {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 10px;
}
.history-end-card li {
  padding: 10px;
  border-radius: 10px;
  background: #f7f8f7;
}
.history-end-card li > div {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
}
.history-end-card li strong {
  overflow: hidden;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.history-end-card li span,
.history-end-card li p {
  color: #6b7280;
  font-size: 11px;
}
.history-end-card li p {
  margin-top: 3px;
}
@media (max-width: 1150px) {
  .history-three-cols {
    min-width: 710px;
  }
  .history-day-metrics {
    gap: 16px;
  }
}
@media (max-width: 1487px) {
  .history-header {
    grid-template-columns: minmax(0, 1fr) auto;
    grid-template-areas:
      'heading meta'
      'controls controls';
    align-items: center;
  }
}
@media (max-width: 850px) and (min-width: 761px) {
  .history-controls {
    flex-wrap: wrap;
  }
}
@media (max-width: 760px) {
  .history-header {
    padding: 20px 16px;
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas:
      'heading'
      'meta'
      'controls';
    gap: 12px;
  }
  .history-content {
    padding: 0 16px 24px;
  }
  .history-summary {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 18px 0;
  }
  .history-summary-cell:nth-child(odd) {
    border-left: 0;
  }
  .history-day-head,
  .history-time-heading,
  .history-timeline-heading {
    align-items: flex-start;
    flex-direction: column;
  }
  .history-end-grid {
    grid-template-columns: 1fr;
  }
  .history-control-meta,
  .history-controls {
    width: 100%;
  }
  .history-control-meta {
    justify-content: space-between;
  }
  .history-controls {
    align-items: stretch;
    flex-direction: column;
    gap: 12px;
  }
  .history-periods {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;
  }
  .history-periods button {
    width: 100%;
  }
  .history-dates {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;
  }
  .history-dates label {
    align-items: stretch;
    flex-direction: column;
    gap: 6px;
  }
  .history-dates input {
    width: 100%;
  }
}
</style>
